import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import BackendHealthBanner from '../components/BackendHealthBanner';
import api from '../utils/api';
import { resolveRedirect, saveIntendedRoute, consumeIntendedRoute } from '../utils/postAuthRedirect';
import roleHome from '../utils/roleHome';

/**
 * FacebookLoginButton — 2026-08-30 layout-stability fix.
 *
 * PRIOR BUG (owner P0 report): the button flashed on first paint and
 * disappeared once `/api/auth/social/facebook/status` resolved with
 * `{configured: false}` because the component `return null`-ed itself.
 * That violated the "layout must remain stable" rule.
 *
 * FIX: the button is ALWAYS rendered. State machine:
 *   • configured === null   → button visible + disabled + "Loading…"
 *   • configured === true   → button visible + enabled
 *   • configured === false  → button visible + disabled + toast on click
 *
 * The `configured === false` branch still preserves DOM height / layout
 * so no shifting, flashing, or disappearance occurs. The click handler
 * surfaces a clear message instead of silently doing nothing.
 */
const FacebookLoginButton = () => {
  const [configured, setConfigured] = useState(null);
  const [fbAppId, setFbAppId] = useState('');
  useEffect(() => {
    let cancelled = false;
    api.get('/auth/social/facebook/status')
      .then((r) => {
        if (cancelled) return;
        setConfigured(!!r.data?.configured);
        if (r.data?.app_id) setFbAppId(r.data.app_id);
      })
      .catch(() => { if (!cancelled) setConfigured(false); });
    return () => { cancelled = true; };
  }, []);

  const isReady = configured === true;
  const isLoading = configured === null;
  const isUnavailable = configured === false;

  const handleClick = () => {
    if (isUnavailable) {
      toast.error(
        'Facebook Sign In is being enabled on this environment. ' +
        'Please use email or Google to sign in for now.',
      );
      return;
    }
    if (!isReady) return;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
    // REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/callback';
    const appId = fbAppId ||
                  window.__FACEBOOK_APP_ID__ ||
                  process.env.REACT_APP_FACEBOOK_APP_ID || '';
    if (!appId) {
      toast.error(
        'Facebook Sign In is being enabled on this environment. ' +
        'Please use email or Google to sign in for now.',
      );
      return;
    }
    const state = crypto.getRandomValues(new Uint32Array(2)).join('-');
    sessionStorage.setItem('fb_oauth_state', state);
    sessionStorage.setItem('fb_oauth_redirect_uri', redirectUri);
    const url = new URL('https://www.facebook.com/v18.0/dialog/oauth');
    url.searchParams.set('client_id', appId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('state', state);
    url.searchParams.set('scope', 'email,public_profile');
    url.searchParams.set('response_type', 'code');
    window.location.href = url.toString();
  };

  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={handleClick}
      aria-label={
        isReady ? 'Continue with Facebook'
        : isLoading ? 'Continue with Facebook (checking availability)'
        : 'Continue with Facebook (temporarily unavailable)'
      }
      title={
        isUnavailable
          ? 'Facebook Sign In is being enabled — use email or Google for now.'
          : undefined
      }
      className={
        'w-full h-11 rounded-md text-white font-medium transition-all ' +
        'inline-flex items-center justify-center gap-2 border ' +
        (isReady
          ? 'bg-[#1877F2] hover:bg-[#166FE0] border-[#1877F2] cursor-pointer'
          : isLoading
          ? 'bg-[#1877F2]/70 border-[#1877F2]/50 cursor-wait'
          : 'bg-[#1877F2]/60 border-[#1877F2]/40 cursor-not-allowed hover:bg-[#1877F2]/60')
      }
      data-testid="facebook-signin-btn"
      data-facebook-state={
        isReady ? 'ready' : isLoading ? 'loading' : 'unavailable'
      }
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
      </svg>
      {isLoading ? 'Loading Facebook…' : 'Continue with Facebook'}
    </button>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  // Iter-213 — gate the submit button while the backend is unreachable
  // so users don't see misleading "Login failed" toasts during a
  // cold-pod startup.
  const [backendReady, setBackendReady] = useState(true);

  // Preserve referral code if a user lands on /login?ref=CODE (or arrives
  // here from a referral link clicked previously) — Register reads from
  // sessionStorage too, so the code survives both directions.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      try { sessionStorage.setItem('pending_referral_code', ref.trim().toUpperCase()); } catch { /* ignore */ }
    }
    // Persist the intended destination so it survives if the user chooses
    // social login or sign-up from here (both lose the ?redirect= param).
    const redirect = searchParams.get('redirect');
    if (redirect) saveIntendedRoute(redirect);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful!');

      // Honour the intended destination (from ?redirect= or the stored
      // deep link) so tapped job links land on the exact gig after login,
      // never the dashboard. resolveRedirect only returns safe internal
      // paths, preventing open-redirect abuse.
      const dest = resolveRedirect(searchParams.get('redirect'));
      if (dest) {
        consumeIntendedRoute();
        navigate(dest, { replace: true });
        return;
      }

      // Default: dashboard by role.
      if (user.role === 'client') {
        // Iter-209 — pending/rejected clients land on the pending screen
        // (also enforced server-side; this avoids a redirect bounce).
        const status = user.client_approval_status;
        if (status && status !== 'approved') {
          navigate('/client/pending');
        } else {
          navigate('/client/home');
        }
      } else if (user.role === 'talent') {
        // Iter-313k — Verification gating. Talent must complete IDV
        // before accessing the dashboard.
        if (user.idv_status && user.idv_status !== 'approved') {
          navigate('/talent/idv');
        } else {
          navigate('/talent/home');
        }
      } else if (user.role === 'agency') {
        // Iter-225q — agencies are now gated behind admin approval like clients.
        // Pending/rejected agencies land on the same pending screen.
        const status = user.client_approval_status;
        if (status && status !== 'approved') {
          navigate('/client/pending');
        } else {
          navigate('/agency/dashboard');
        }
      } else if (user.role === 'agency_staff') {
        navigate('/agency/crm');
      } else if (user.role === 'admin' || user.role === 'super_admin') {
        navigate('/admin/home');
      } else if (user.role === 'ai_creator') {
        navigate('/creator/dashboard');
      } else {
        // Catch-all — every authenticated role resolves to its home via the
        // shared resolver so an avatar/creator/agency-staff (or any future
        // role) is never stranded on /login after a successful sign-in.
        navigate(roleHome(user.role));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Iter-225j — trapped account (no password_hash). Surface a clear
      // recovery prompt and route the user to /forgot-password with their
      // email pre-filled instead of the generic "Login failed" toast.
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'PASSWORD_NOT_SET') {
        toast.error(detail.message || 'Your account needs a password reset.');
        const email = encodeURIComponent(formData.email.trim());
        navigate(`/forgot-password?email=${email}&reason=password_not_set`);
        return;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center auth-background" data-testid="login-page">
      <BackendHealthBanner onReady={setBackendReady} />
      <Card className="w-full max-w-md glassmorphism border-zinc-800" data-testid="login-form">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-2">
            <img
              src="https://res.cloudinary.com/dzlc6qta0/image/upload/v1771451035/d2e1bd_3ffc4a4bc88e4587b369a584fd8a636b_mv2_pvlple.png"
              alt="iEvntür Logo"
              className="h-12"
            />
          </div>
          <CardTitle className="text-3xl font-bold text-white">Welcome to iEvnt&uuml;r</CardTitle>
          <CardDescription className="text-zinc-400" data-testid="login-tagline">
            <span className="block text-white font-medium mb-1">
              Where talent, brands, and AI come together.
            </span>
            <span className="block">
              Discover opportunities, connect with top talent, and grow with iEvnt&uuml;r.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
                className="h-11 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-white">Password</Label>
                <Link to="/forgot-password" className="text-xs text-cyan-500 hover:text-cyan-400 transition-colors" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
                className="h-11 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-black font-medium transition-all"
              disabled={loading || !backendReady}
              data-testid="login-submit-button"
            >
              {loading
                ? 'Signing in...'
                : !backendReady
                ? 'Waiting for server…'
                : 'Sign In'}
            </Button>
          </form>

          {/* Social sign-in (2026-08-05) — Google + Facebook. These are
              ADDITIONAL login methods — email/password stays available for
              anyone who prefers it. Never touches the user's password. */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">or continue with</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
                // REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href =
                  `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full h-11 border border-zinc-700 rounded-md bg-white text-zinc-900 font-medium hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>
            <FacebookLoginButton />
          </div>

          <div className="mt-6 text-center text-sm">
            <span className="text-zinc-400">Don't have an account? </span>
            <Link
              to={`/register${(() => {
                try { const c = sessionStorage.getItem('pending_referral_code'); return c ? `?ref=${encodeURIComponent(c)}` : ''; } catch { return ''; }
              })()}`}
              className="text-cyan-500 font-medium hover:text-cyan-400 transition-colors"
              data-testid="register-link"
            >
              Sign up
            </Link>
          </div>
          <div className="mt-2 text-center text-xs">
            <Link to="/creator/apply" className="text-fuchsia-400 hover:text-fuchsia-300 transition" data-testid="creator-apply-link">
              Apply as an AI avatar creator →
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;